import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alan, Buton, cn } from "@koclukpanel/ui";
import { useSudoKapisi } from "./SudoKapisi";
import {
  ApiHatasi,
  tekKayitOnizle,
  tekKayitUygula,
  type TekKayitOnizlemesi,
  type TekKayitYazmaSonucu,
} from "./api";

/**
 * TEK KAYIT KURTARMA — F-YED-27
 *
 * ================================================================
 * SIHIRBAZIN ADIMLARINA KARISMIYOR
 * ================================================================
 * Geri yukleme sihirbazi "kapsam sec → yedek sec → etkiyi gor → yaz"
 * akisini yuruttugu icin bu is oraya sigmiyor: burada iki adim var
 * (onizle, yaz) ve aradaki karar tamamen farkli bir soruya dayaniyor —
 * "bu fark gercekten geri alinmali mi".
 *
 * ================================================================
 * ONIZLEME OLMADAN YAZMA DUGMESI YOK
 * ================================================================
 * Yazma dugmesi yalnizca onizleme geldikten SONRA ciziliyor ve
 * `parmakIzi`ni tasiyor. Sunucu zaten dogruluyor ama ekranin da
 * dogru sirayi dayatmasi gerekiyor: kullaniciya "istersen bakmadan
 * yaz" secenegi sunmak, sartnamenin acikca tanimladigi sirayi
 * bozardi.
 */

function deger(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

function Fark({ onizleme }: { onizleme: TekKayitOnizlemesi }) {
  const k = onizleme.karar;

  return (
    <div className="flex flex-col gap-3">
      <p
        className={cn(
          "rounded-kart p-3 text-sm",
          k.tur === "DEGISECEK" && k.uretimDahaYeni
            ? "border-uyari-200 bg-uyari-50 text-uyari-800 border"
            : onizleme.yazilabilir
              ? "border-notr-200 bg-notr-50 text-notr-700 border"
              : "border-notr-200 bg-notr-0 text-notr-600 border",
        )}
      >
        {onizleme.cumle}
      </p>

      {k.tur === "DEGISECEK" && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-notr-600 text-xs">
                <th className="py-1 pr-3 font-medium">Alan</th>
                <th className="py-1 pr-3 font-medium">Yedekteki</th>
                <th className="py-1 font-medium">Şu an üretimde</th>
              </tr>
            </thead>
            <tbody>
              {k.farklar.map((f) => (
                <tr key={f.alan} className="border-notr-100 border-t align-top">
                  <td className="text-notr-700 py-1.5 pr-3 font-mono text-xs">{f.alan}</td>
                  {/*
                   * Yedekteki deger YESIL degil: "yazilacak olan"
                   * dogru olan demek degil. Renk vermek, kullaniciyi
                   * bakmadan onaylamaya iterdi.
                   */}
                  <td className="text-notr-800 py-1.5 pr-3 break-all">{deger(f.yedekteki)}</td>
                  <td className="text-notr-800 py-1.5 break-all">{deger(f.uretimdeki)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function TekKayitKurtarma({ runId }: { runId: string }) {
  const [tablo, setTablo] = useState("");
  const [kayitId, setKayitId] = useState("");
  const [onizleme, setOnizleme] = useState<TekKayitOnizlemesi | null>(null);
  const [sonuc, setSonuc] = useState<TekKayitYazmaSonucu | null>(null);
  const { hataYakala, kapi } = useSudoKapisi(
    "Yedekten kayıt çıkarmak üretim verisi okur. Devam etmeden önce şifrenizi doğrulayın.",
  );

  const onizle = useMutation({
    mutationFn: () => tekKayitOnizle({ runId, tablo: tablo.trim(), kayitId: kayitId.trim() }),
    onSuccess: (d) => {
      setOnizleme(d);
      setSonuc(null);
    },
    /*
     * Sudo acilinca ISLEM TEKRARLANIYOR. Ilk yazimda bos birakmistim
     * ve canli denemede goruldu: sifre kabul ediliyor, kapi kapaniyor,
     * SONRA HICBIR SEY OLMUYOR. Kullanici dogru sifreyi girdigi halde
     * dugmeye ikinci kez basmak zorunda kaliyor ve o an "kabul etmedi
     * mi" diye dusunuyor.
     */
    onError: hataYakala(() => onizle.mutate()),
  });

  const uygula = useMutation({
    mutationFn: () =>
      tekKayitUygula({
        runId,
        tablo: onizleme!.tablo,
        kayitId: onizleme!.kayitId,
        parmakIzi: onizleme!.parmakIzi,
      }),
    onSuccess: (d) => {
      setSonuc(d);
      /*
       * Yazdiktan sonra onizleme TEMIZLENIYOR. Ekranda birakmak,
       * artik gecerli olmayan bir farki gosterip ikinci kez
       * yazdirmaya davet ederdi.
       */
      setOnizleme(null);
    },
    onError: hataYakala(() => uygula.mutate()),
  });

  const hazir = runId !== "" && tablo.trim().length > 0 && kayitId.trim().length > 0;

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="text-notr-900 text-sm font-semibold">Tek kayıt kurtarma</h3>
        <p className="text-notr-600 mt-0.5 text-xs">
          Bütün sistemi geri yüklemeden tek bir kaydı yedekten getirir. Önce farkı gösterir; yazma
          ancak siz farkı gördükten sonra yapılır.
        </p>
      </div>

      {runId === "" ? (
        // Yedek secilmeden tablo/kimlik sormak, doldurulup sonra
        // "hangi yedekten" diye geri donmek demekti.
        <p className="text-notr-600 text-sm">Önce yukarıdan bir yedek seçin.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <Alan
              etiket="Tablo"
              value={tablo}
              onChange={(e) => setTablo(e.target.value)}
              placeholder="Student"
              className="font-mono"
            />
            <div className="flex-1">
              <Alan
                etiket="Kayıt kimliği"
                value={kayitId}
                onChange={(e) => setKayitId(e.target.value)}
                placeholder="cms..."
                className="font-mono"
              />
            </div>
          </div>

          <div>
            <Buton
              gorunum="ikincil"
              disabled={!hazir || onizle.isPending}
              onClick={() => onizle.mutate()}
            >
              {onizle.isPending ? "Yedek açılıyor…" : "Farkı göster"}
            </Buton>
          </div>

          {onizle.isError && (
            <p className="text-hata-500 text-xs">
              {onizle.error instanceof ApiHatasi ? onizle.error.message : "Önizleme alınamadı."}
            </p>
          )}

          {onizleme !== null && (
            <>
              <Fark onizleme={onizleme} />

              {onizleme.yazilabilir && (
                <div>
                  <Buton
                    gorunum="tehlike"
                    disabled={uygula.isPending}
                    onClick={() => uygula.mutate()}
                  >
                    {uygula.isPending ? "Yazılıyor…" : "Bu farkı üretime yaz"}
                  </Buton>
                </div>
              )}
            </>
          )}

          {uygula.isError && (
            <p className="text-hata-500 text-xs">
              {uygula.error instanceof ApiHatasi ? uygula.error.message : "Yazma yapılamadı."}
            </p>
          )}

          {sonuc !== null && (
            <p className="rounded-kart shadow-kart border-onay-200 bg-onay-50 text-onay-700 border p-3 text-sm">
              {sonuc.islem === "EKLENDI" ? "Kayıt geri kondu" : "Kayıt güncellendi"} —{" "}
              {sonuc.yazilanAlan} alan. İşlem denetim kaydına yazıldı.
            </p>
          )}

          {kapi}
        </>
      )}
    </section>
  );
}
